// Path: C:\gestion-copro\src\pages\apporteur\ApporteurCommissionsPage.tsx
// Session 188 : Page commissions — vue apporteur (lecture seule)
// Session 207 : Badge "Non lié" sur les convertis sans clientUserId branché
// Session 224 : Refonte — lecture depuis crm_commissions/ (plus de calcul à la volée)
// Session 224 : Ajout onglet Guide commissions

import React, { useState, useEffect, useCallback } from 'react';
import {
  Coins,
  TrendingUp,
  CheckCircle2,
  Clock,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  BadgeCheck,
  AlertTriangle,
  CalendarDays,
  BookOpen,
  HelpCircle,
} from 'lucide-react';
import {
  Commission,
  StatutCommission,
  LABELS_STATUT_COMMISSION,
  TAUX_COMMISSION,
  ResponsableProspect,
} from '../../types/crm.types';
import { chargerCommissions } from '../../services/crm.service';
import { useAuth } from '../../context/AuthContext';

// ============================================
// HELPERS
// ============================================

const formatEur = (n: number) =>
  n.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });

const COULEURS_STATUT: Record<StatutCommission, string> = {
  du: 'bg-amber-100 text-amber-800 border-amber-300',
  partiel: 'bg-blue-100 text-blue-800 border-blue-300',
  verse: 'bg-emerald-100 text-emerald-800 border-emerald-300',
};

// ============================================
// CARTE TRIMESTRE
// ============================================

interface CarteTrimestreProps {
  commission: Commission;
}

const CarteTrimestre: React.FC<CarteTrimestreProps> = ({ commission }) => {
  const [ouvert, setOuvert] = useState(false);

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <div className="p-4 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
            <CalendarDays className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <p className="font-semibold text-gray-800">{commission.trimestre}</p>
            {commission.statut === 'verse' && commission.dateVersement && (
              <p className="text-xs text-gray-400">
                Verse le {commission.dateVersement.toDate().toLocaleDateString('fr-FR')}
                {commission.referenceVersement ? ` — Ref. ${commission.referenceVersement}` : ''}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          <span className={`text-xs px-2 py-1 rounded-full border font-medium ${COULEURS_STATUT[commission.statut]}`}>
            {LABELS_STATUT_COMMISSION[commission.statut]}
          </span>

          <div className="text-right">
            <p className="font-bold text-gray-900">{formatEur(commission.montantDu)}</p>
            {commission.statut === 'verse' && (
              <div className="flex items-center gap-1 text-emerald-600 text-xs justify-end">
                <BadgeCheck className="w-3 h-3" />
                Verse integralement
              </div>
            )}
            {commission.statut === 'partiel' && (
              <p className="text-xs text-amber-600">Reste : {formatEur(commission.resteAVerser)}</p>
            )}
            {commission.statut === 'du' && (
              <p className="text-xs text-amber-600">En attente de versement</p>
            )}
          </div>

          <button
            onClick={() => setOuvert(!ouvert)}
            className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
          >
            {ouvert ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {ouvert && (
        <div className="border-t border-gray-100 p-4">
          {commission.details.length === 0 ? (
            <p className="text-sm text-gray-400 italic">Aucun client actif ce trimestre.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500 uppercase border-b border-gray-100">
                  <th className="text-left pb-2">Client</th>
                  <th className="text-left pb-2">Formule</th>
                  <th className="text-left pb-2">Statut</th>
                  <th className="text-right pb-2">Commission/mois</th>
                  <th className="text-right pb-2">Commission trimestre</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {commission.details.map((d) => (
                  <tr key={d.userId}>
                    <td className="py-2 font-medium text-gray-800">{d.nomClient}</td>
                    <td className="py-2 text-gray-600">{d.formule || 'Non renseigne'}</td>
                    <td className="py-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        d.statutAbonnement === 'actif'
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {d.statutAbonnement}
                      </span>
                    </td>
                    <td className="py-2 text-right text-gray-700">{formatEur(d.commissionMensuelle)}</td>
                    <td className="py-2 text-right font-semibold text-blue-700">
                      {formatEur(d.commissionMensuelle * 3)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-gray-200">
                  <td colSpan={4} className="pt-2 text-right text-sm font-semibold text-gray-700">
                    Total trimestre :
                  </td>
                  <td className="pt-2 text-right font-bold text-blue-800">
                    {formatEur(commission.montantDu)}
                  </td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================
// ONGLET GUIDE COMMISSIONS
// ============================================

const etapesGuide = [
  {
    etape: '1',
    titre: 'Tu convertis un prospect',
    desc: 'Ton prospect souscrit un abonnement Stripe. Dominique fait le rapprochement entre ton prospect et son compte Copro Protect.',
  },
  {
    etape: '2',
    titre: 'La commission est calculee',
    desc: 'En fin de trimestre, Dominique genere le calcul. Tous tes clients avec un abonnement actif et encaisse sont comptabilises.',
  },
  {
    etape: '3',
    titre: 'Tu vois le detail ici',
    desc: 'La commission apparait dans ton historique avec le statut "Du". Tu peux deplier chaque trimestre pour voir le detail client par client.',
  },
  {
    etape: '4',
    titre: 'Tu emets une facture',
    desc: "Pour recevoir le versement, envoie une facture d'apporteur d'affaires a Dominique. Le virement suit sous 15 jours ouvres.",
  },
];

const statutsGuide = [
  {
    label: 'A recevoir',
    couleur: 'bg-amber-100 text-amber-800',
    desc: 'Calcule et valide par Dominique, versement en attente de ta facture.',
  },
  {
    label: 'Partiellement verse',
    couleur: 'bg-blue-100 text-blue-800',
    desc: 'Un acompte a ete verse, le solde reste du.',
  },
  {
    label: 'Verse',
    couleur: 'bg-emerald-100 text-emerald-800',
    desc: 'Integralement paye. Date et reference de virement visibles en depliant la carte.',
  },
];

const pointsAugmente = ['Nouveaux clients actifs', 'Clients qui renouvellent', 'Passage a une formule superieure'];
const pointsReduit = ["Client qui annule son abonnement", 'Impaye prolonge (Stripe desactive)', 'Prospect converti non rapproche'];

const OngletGuideCommissions: React.FC = () => {
  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-6 text-white">
        <div className="flex items-center gap-3 mb-2">
          <BookOpen size={28} />
          <h2 className="text-2xl font-bold">Guide de tes commissions</h2>
        </div>
        <p className="text-blue-100 text-sm">
          Tout ce qu'il faut savoir pour suivre et comprendre tes remunerations Copro Protect.
        </p>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
        <p className="font-bold text-gray-800 mb-3 flex items-center gap-2">
          <HelpCircle size={16} className="text-blue-500" />
          Comment ca marche
        </p>
        <p className="text-sm text-gray-600 mb-4">
          Tu touches <strong>{Math.round(TAUX_COMMISSION * 100)}% du CA HT</strong> pour chaque client que tu as amene, tant que son abonnement Stripe est actif. Le versement est trimestriel.
        </p>
        {etapesGuide.map((item) => (
          <div key={item.etape} className="flex gap-3 mb-3 last:mb-0">
            <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 font-bold text-sm flex items-center justify-center flex-shrink-0">
              {item.etape}
            </div>
            <div>
              <p className="font-semibold text-gray-800 text-sm">{item.titre}</p>
              <p className="text-xs text-gray-500 mt-0.5">{item.desc}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
        <p className="font-bold text-gray-800 mb-3">Lire ton historique</p>
        {statutsGuide.map((item) => (
          <div key={item.label} className="flex items-start gap-3 mb-3 last:mb-0">
            <span className={`text-xs px-2 py-1 rounded-full font-medium flex-shrink-0 ${item.couleur}`}>
              {item.label}
            </span>
            <p className="text-sm text-gray-600">{item.desc}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
        <p className="font-bold text-gray-800 mb-3">Ce qui conditionne le montant</p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-2">Ce qui augmente</p>
            {pointsAugmente.map((item) => (
              <div key={item} className="flex items-center gap-2 mb-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
                <p className="text-xs text-gray-600">{item}</p>
              </div>
            ))}
          </div>
          <div>
            <p className="text-xs font-semibold text-red-600 uppercase tracking-wide mb-2">Ce qui reduit</p>
            {pointsReduit.map((item) => (
              <div key={item} className="flex items-center gap-2 mb-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" />
                <p className="text-xs text-gray-600">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
        <p className="font-bold text-blue-800 text-sm mb-2">La piece a fournir</p>
        <p className="text-sm text-blue-700 mb-3">
          Pour recevoir ton versement, emets une facture d'apporteur d'affaires a :
        </p>
        <div className="bg-white rounded-lg p-3 text-xs text-gray-700 font-mono border border-blue-100 space-y-1">
          <p>DOM TECH &amp; SERVICES (SASU)</p>
          <p>RCS 939 094 231 Boulogne-sur-Mer</p>
          <p>Objet : Commission apporteur - [Trimestre]</p>
          <p>Montant : [Montant du] HT</p>
        </div>
        <p className="text-xs text-blue-500 mt-2">Virement sous 15 jours ouvres apres reception.</p>
      </div>
    </div>
  );
};

// ============================================
// PAGE PRINCIPALE
// ============================================

const ApporteurCommissionsPage: React.FC = () => {
  const { userProfile } = useAuth();
  const [onglet, setOnglet] = useState<'commissions' | 'guide'>('commissions');
  const [commissions, setCommissions] = useState<Commission[]>([]);
  const [loading, setLoading] = useState(true);

  const apporteurId: ResponsableProspect =
    (userProfile?.apporteurInfo?.identifiant as ResponsableProspect) || 'alain';

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const data = await chargerCommissions(apporteurId);
      setCommissions(data);
    } finally {
      setLoading(false);
    }
  }, [apporteurId]);

  useEffect(() => {
    charger();
  }, [charger]);

  const totalDu = commissions
    .filter((c) => c.statut !== 'verse')
    .reduce((s, c) => s + c.resteAVerser, 0);

  const totalVerse = commissions
    .filter((c) => c.statut === 'verse')
    .reduce((s, c) => s + c.montantVerse, 0);

  const totalGenere = commissions.reduce((s, c) => s + c.montantDu, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Mes commissions</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {Math.round(TAUX_COMMISSION * 100)}% du CA HT recurrent — versement trimestriel
          </p>
        </div>
        <button
          onClick={charger}
          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <RefreshCw size={18} />
        </button>
      </div>

      {/* Onglets */}
      <div className="flex gap-2 border-b border-gray-200">
        {([
          { id: 'commissions' as const, label: 'Mes commissions', icon: <Coins size={16} /> },
          { id: 'guide' as const, label: 'Guide', icon: <BookOpen size={16} /> },
        ]).map((o) => (
          <button
            key={o.id}
            onClick={() => setOnglet(o.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
              onglet === o.id
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {o.icon}
            {o.label}
          </button>
        ))}
      </div>

      {/* Guide */}
      {onglet === 'guide' && <OngletGuideCommissions />}

      {/* Commissions */}
      {onglet === 'commissions' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white border border-gray-100 rounded-xl shadow-sm p-5 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">A recevoir</p>
                <p className="text-2xl font-bold text-amber-700">{formatEur(totalDu)}</p>
              </div>
            </div>

            <div className="bg-white border border-gray-100 rounded-xl shadow-sm p-5 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Total recu</p>
                <p className="text-2xl font-bold text-emerald-700">{formatEur(totalVerse)}</p>
              </div>
            </div>

            <div className="bg-white border border-gray-100 rounded-xl shadow-sm p-5 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Total genere</p>
                <p className="text-2xl font-bold text-blue-700">{formatEur(totalGenere)}</p>
                <p className="text-xs text-gray-400">
                  {commissions.length} trimestre{commissions.length > 1 ? 's' : ''}
                </p>
              </div>
            </div>
          </div>

          {commissions.length === 0 && (
            <div className="bg-white border border-dashed border-gray-200 rounded-xl p-10 text-center">
              <Coins className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="font-semibold text-gray-600">Aucune commission enregistree</p>
              <p className="text-sm text-gray-400 mt-1 max-w-md mx-auto">
                Les commissions sont calculees chaque trimestre par Dominique et apparaitront ici des le premier trimestre clos.
              </p>
            </div>
          )}

          {totalDu > 0 && (
            <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
              <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-800">
                  {formatEur(totalDu)} en attente de versement
                </p>
                <p className="text-xs text-amber-700 mt-0.5">
                  Le versement est effectue sur presentation de ta facture d'apporteur d'affaires.
                </p>
              </div>
            </div>
          )}

          {commissions.length > 0 && (
            <div className="space-y-3">
              <h2 className="font-semibold text-gray-700 text-sm uppercase tracking-wide">
                Historique par trimestre
              </h2>
              {commissions.map((c) => (
                <CarteTrimestre key={c.id} commission={c} />
              ))}
            </div>
          )}

          <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
            <h3 className="font-semibold text-blue-800 text-sm mb-2">Conditions de commissionnement</h3>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>Commission de {Math.round(TAUX_COMMISSION * 100)}% du CA HT tant que le client est abonne actif</li>
              <li>Versement trimestriel sur presentation d'une facture d'apporteur d'affaires</li>
              <li>Commission due uniquement sur les abonnements Stripe actifs et encaisses</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApporteurCommissionsPage;
